import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-base flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-4">
          <img src="/semicon-keralam-logo.png" alt="Semicon Keralam" className="h-14 w-auto" />
          <div className="border-l border-ink/15 pl-4">
            <h1 className="font-display font-black text-ink text-xl flex items-center gap-2">
              <Lock className="w-4 h-4 text-teal" aria-hidden="true" /> Admin sign-in
            </h1>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink/70">Semicon Keralam · content management</div>
          </div>
        </div>
        <form onSubmit={submit} className="border border-ink/10 bg-surface p-8 space-y-4" data-testid="admin-login-form" aria-label="Admin sign-in">
          <div>
            <label htmlFor="admin-email-input" className="block text-xs font-mono uppercase tracking-widest text-ink/70 mb-2">Email</label>
            <input
              id="admin-email-input"
              data-testid="admin-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-4 py-3 text-ink text-sm"
            />
          </div>
          <div>
            <label htmlFor="admin-password-input" className="block text-xs font-mono uppercase tracking-widest text-ink/70 mb-2">Password</label>
            <input
              id="admin-password-input"
              data-testid="admin-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-4 py-3 text-ink text-sm"
            />
          </div>
          {error && <div data-testid="admin-login-error" role="alert" className="text-red-400 text-sm">{error}</div>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy}
            className="w-full bg-brand hover:bg-brand-dark disabled:opacity-50 text-ink py-3.5 font-medium"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}
